import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutData } from '@/mocks/about';

export default function AboutPage() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = '关于我们 - 快猫短视频';
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');

    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormSubmitted(true);
      return;
    }

    const formData = new FormData(form);
    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9rg6ivnahb7382ovm5g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormSubmitted(true);
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg);
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  const orgLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '快猫短视频',
    url: 'https://www.kuaimao.com',
    description: '快猫短视频 - 国内领先的短视频内容平台',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '未来科技城快猫大厦',
      addressLocality: '杭州',
      addressRegion: '浙江',
      postalCode: '311100',
      addressCountry: 'CN',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '0571-8888-6666',
      email: 'contact@kuaimao.com',
      contactType: 'customer service',
    },
    sameAs: [
      'https://weibo.com/kuaimao',
      'https://www.zhihu.com/org/kuaimao',
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgLd)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="w-full py-12 md:py-16 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3">
                  {aboutData.brand.title}
                </h1>
                <p className="text-base md:text-lg text-primary-600 font-medium mb-4">
                  {aboutData.brand.subtitle}
                </p>
                <p className="text-sm md:text-base text-foreground-600 max-w-3xl mx-auto leading-relaxed">
                  {aboutData.brand.description}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-16">
                <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <h3 className="text-sm font-semibold text-foreground-500 mb-2">我们的使命</h3>
                  <p className="text-base font-bold text-foreground-950">{aboutData.brand.mission}</p>
                </div>
                <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <h3 className="text-sm font-semibold text-foreground-500 mb-2">我们的愿景</h3>
                  <p className="text-base font-bold text-foreground-950">{aboutData.brand.vision}</p>
                </div>
              </div>

              <div className="mb-16">
                <h2 className="text-xl font-bold font-heading text-foreground-950 mb-6 text-center">核心价值观</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {aboutData.brand.values.map((v) => (
                    <div
                      key={v.title}
                      className="p-5 rounded-xl bg-background-100 border border-background-200/70 text-center"
                    >
                      <h3 className="text-base font-semibold text-foreground-950 mb-2">{v.title}</h3>
                      <p className="text-sm text-foreground-500">{v.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="w-full py-12 md:py-16 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <h2 className="text-xl font-bold font-heading text-foreground-950 mb-8 text-center">发展历程</h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
                <div className="flex flex-col gap-6">
                  {aboutData.milestones.map((m, idx) => (
                    <div
                      key={idx}
                      className={`relative flex items-start gap-4 md:gap-8 ${
                        idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className={`flex-1 ${idx % 2 === 0 ? 'md:text-right' : 'md:text-left'} pl-10 md:pl-0`}>
                        <div className="p-4 rounded-xl bg-background-50 border border-background-200/70 inline-block">
                          <span className="text-xs font-semibold text-primary-600">{m.year}</span>
                          <p className="text-sm text-foreground-700 mt-1">{m.event}</p>
                        </div>
                      </div>
                      <div className="absolute left-[9px] md:left-1/2 md:-translate-x-1/2 w-2.5 h-2.5 rounded-full bg-primary-500 border-2 border-background-50 mt-5 flex-shrink-0"></div>
                      <div className="flex-1 hidden md:block"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="w-full py-12 md:py-16 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-5xl mx-auto">
              <h2 className="text-xl font-bold font-heading text-foreground-950 mb-8 text-center">
                {aboutData.contact.title}
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-10">
                {aboutData.contact.subtitle}
              </p>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="flex flex-col gap-4">
                  <div className="p-4 rounded-xl bg-background-100 border border-background-200/70 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">公司地址</p>
                      <p className="text-sm font-medium text-foreground-900">{aboutData.contact.address}</p>
                    </div>
                  </div>
                  <div className="p-4 rounded-xl bg-background-100 border border-background-200/70 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">联系电话</p>
                      <p className="text-sm font-medium text-foreground-900">{aboutData.contact.phone}</p>
                    </div>
                  </div>
                  <div className="p-4 rounded-xl bg-background-100 border border-background-200/70 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-secondary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">电子邮箱</p>
                      <p className="text-sm font-medium text-foreground-900">{aboutData.contact.email}</p>
                    </div>
                  </div>
                  <div className="p-4 rounded-xl bg-background-100 border border-background-200/70 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">工作时间</p>
                      <p className="text-sm font-medium text-foreground-900">{aboutData.contact.workingHours}</p>
                    </div>
                  </div>
                  <div className="flex gap-1.5 mt-1">
                    {aboutData.contact.socialMedia.map((sm) => (
                      <a
                        key={sm.name}
                        href="#"
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-background-100 text-xs text-foreground-600 hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer"
                      >
                        <i className={sm.icon}></i>
                        {sm.name}
                      </a>
                    ))}
                  </div>
                </div>

                <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <h3 className="text-base font-semibold text-foreground-950 mb-4">发送消息</h3>

                  {formSubmitted ? (
                    <div className="flex flex-col items-center justify-center py-10">
                      <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mb-4">
                        <i className="ri-check-line text-primary-600 text-2xl"></i>
                      </div>
                      <p className="text-base font-medium text-foreground-900 mb-1">消息已发送</p>
                      <p className="text-sm text-foreground-500">感谢你的留言，我们会尽快回复！</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} data-readdy-form="true" className="flex flex-col gap-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          姓名 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入你的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          邮箱 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入你的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          留言内容 <span className="text-primary-500">*</span>
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          rows={4}
                          maxLength={500}
                          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                          placeholder="请输入你的留言内容（最多500字）"
                        ></textarea>
                      </div>
                      <input
                        name="phone_alt"
                        type="text"
                        className="honeypot-field"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                      {formError && (
                        <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{formError}</p>
                      )}
                      <button
                        type="submit"
                        className="w-full py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        发送消息
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}